import { ExclamationCircleOutlined, InboxOutlined, AppstoreOutlined, CalendarOutlined, TeamOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Tooltip } from 'antd';
import { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskDatePicker from './TaskDatePicker';

interface SplitRoutePublishConfirmModalProps {
    visible: boolean;
    onCancel: () => void;
    /**
     * 点「确认」：回传所选派送日期，由调用方按最新仓库时间复核并继续发布流程。
     * 返回 false 表示所选日期已不可选，弹窗清空日期并提示重新选择。
     */
    onConfirm: (taskDate: Dayjs) => Promise<boolean>;
    /** 仓库当前时间，决定日期面板中哪些日期可选 */
    warehouseNow: Dayjs;
    areaName: string;
    stats: {
        segments: number;
        drivers: number;
        assigned: number;
        unassigned: number;
    };
}

const SplitRoutePublishConfirmModal: React.FC<SplitRoutePublishConfirmModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    warehouseNow,
    areaName,
    stats,
}) => {
    const { t } = useTranslation();
    const [taskDate, setTaskDate] = useState<Dayjs | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    // 每次打开都重新留空，必须由用户主动选择
    useEffect(() => {
        if (visible) {
            setTaskDate(null);
            setDateError(null);
            setConfirming(false);
        }
    }, [visible]);

    const handleConfirm = async () => {
        if (!taskDate) return;
        setConfirming(true);
        try {
            const accepted = await onConfirm(taskDate);
            if (!accepted) {
                setTaskDate(null);
                setDateError(t('delivery.routes.message.deliveryDateChanged'));
            }
        } finally {
            setConfirming(false);
        }
    };

    const regionPrefix = areaName ? areaName.charAt(0).toUpperCase() : '';

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onCancel}
            width={500}
            footer={null}
            closable={false}
            destroyOnClose
        >
            <div className="p-4">
                <div className="flex items-center gap-3 mb-8">
                    <ExclamationCircleOutlined className="text-3xl text-red-500" />
                    <span className="text-2xl font-bold text-black">
                        {t('delivery.routes.message.publishConfirmTitle')}
                    </span>
                </div>

                <div className="mb-4 pl-6">
                    <div className="flex items-center mb-3">
                        <AppstoreOutlined className="text-xl mr-2 text-gray-500" />
                        <span className="text-lg text-black font-medium">
                            {t('delivery.routes.message.area', { defaultValue: '区域' })}：{areaName}
                        </span>
                    </div>
                    <div className="flex items-center mb-3">
                        <InboxOutlined className="text-xl mr-2 text-gray-500" />
                        <span className="text-lg text-black font-medium">
                            {t('delivery.routes.message.totalParcels', { defaultValue: '包裹总数' })}：{stats.assigned}
                        </span>
                    </div>
                </div>

                <div className="mb-4 pl-6">
                    {/* 标签过长（英 / 西）时日期框自动换行并与标签左对齐，避免撑出弹窗 */}
                    <div className="relative pl-8 mb-2">
                        <CalendarOutlined className="absolute left-0 top-1.5 text-xl text-gray-500" />
                        <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
                            <span className="text-lg font-medium mt-0.5">
                                {t('delivery.routes.message.selectedDeliveryDate')}
                                <Tooltip title={<div className="whitespace-pre-line">{t('delivery.routes.message.taskDateRuleTooltip')}</div>}>
                                    <QuestionCircleOutlined className="cursor-help ml-1 text-base" style={{ color: '#7c3aed' }} />
                                </Tooltip>
                                :
                            </span>
                            <TaskDatePicker
                                value={taskDate}
                                onChange={(date) => { setTaskDate(date); setDateError(null); }}
                                warehouseNow={warehouseNow}
                                placeholder={t('delivery.routes.message.selectDeliveryDatePlaceholder')}
                                errorText={dateError}
                            />
                        </div>
                    </div>
                    <div className="pl-8">
                        <span className="text-sm text-red-500">
                            {t('delivery.routes.message.publishErrorWarning')}
                        </span>
                    </div>
                </div>

                <div className="flex items-center mb-6 pl-6">
                    <TeamOutlined className="text-xl mr-2 text-gray-500" />
                    <span className="text-lg text-black font-medium">
                        {t('delivery.routes.message.assignedDriverCount')}: {stats.drivers}
                    </span>
                </div>

                <div className="flex justify-end gap-3">
                    <Button size="large" onClick={onCancel} className="min-w-[120px]">
                        {t('delivery.routes.message.returnToModify')}
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleConfirm}
                        disabled={!taskDate}
                        loading={confirming}
                        className="min-w-[120px]"
                        style={taskDate ? { background: '#7c3aed' } : undefined}
                    >
                        {t('delivery.routes.message.confirmPublish')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SplitRoutePublishConfirmModal;
